import {
  ProxyHistoryInputSchema,
  ProxyHistoryOutputSchema,
  PROXY_HISTORY_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import type { HistoryFilters } from '../../../../electron/search-db';
import { mapRequestMetadataRowToProxyHistoryRow } from '../../../../utils/proxyHistoryRow.utils';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
  resolvePortalTargetScope,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler, PortalToolHandlerResult } from '../types';

function buildHistoryFilters({
  host,
  method,
  statusCode,
  statusMin,
  statusMax,
  search,
  sources,
  excludeProxyModified,
}: {
  host?: string;
  method?: string;
  statusCode?: number;
  statusMin?: number;
  statusMax?: number;
  search?: string;
  sources?: string[];
  excludeProxyModified?: boolean;
}): HistoryFilters {
  const filters: HistoryFilters = {};

  if (host) {
    filters.host = host;
  }
  if (method) {
    filters.method = method;
  }
  if (statusCode !== undefined) {
    filters.statusCode = statusCode;
  } else {
    const resolvedStatusMin =
      statusMin ?? (statusMax !== undefined ? 0 : undefined);
    const resolvedStatusMax =
      statusMax ?? (statusMin !== undefined ? 600 : undefined);
    if (resolvedStatusMin !== undefined && resolvedStatusMax !== undefined) {
      filters.statusMin = resolvedStatusMin;
      filters.statusMax = resolvedStatusMax;
    }
  }
  if (search) {
    filters.search = search;
  }
  if (sources && sources.length > 0) {
    filters.sources = sources;
  }
  if (excludeProxyModified) {
    filters.excludeProxyModified = true;
  }

  return filters;
}

function toProxyHistoryToolResult({
  message,
  data,
}: {
  message: string;
  data: {
    rows: ReturnType<typeof mapRequestMetadataRowToProxyHistoryRow>[];
    total: number;
    skip: number;
    limit: number;
  };
}): PortalToolHandlerResult {
  const parsed = ProxyHistoryOutputSchema.safeParse({ message, data });

  if (!parsed.success) {
    return {
      status: 'error',
      result: {
        message: 'Proxy history result did not match the expected shape',
      },
    };
  }

  return {
    status: 'success',
    result: parsed.data,
  };
}

export const proxyHistoryHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: ProxyHistoryInputSchema,
    input,
    toolName: PROXY_HISTORY_TOOL_NAME,
    toolCallId,
    batchId,
  });
  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable() || !window.electronAPI.history?.getRows) {
    return getElectronApiUnavailableResult({
      toolName: PROXY_HISTORY_TOOL_NAME,
      toolCallId,
      batchId,
      message: 'History API is not available',
    });
  }

  const { targetId, targetType } = resolvePortalTargetScope({ applicationId });
  const skip = parsedInput.data.skip ?? 0;
  const limit = parsedInput.data.limit ?? 20;

  switch (parsedInput.data.action) {
    case 'list': {
      const result = await window.electronAPI.history.getRows({
        targetId,
        targetType,
        startRow: skip,
        endRow: skip + limit,
        sortModel: [{ colId: 'timestamp', sort: 'desc' }],
        filters: buildHistoryFilters(parsedInput.data),
      });

      if (!result.success || !result.data) {
        return {
          status: 'error',
          result: {
            message: result.error || 'Failed to list proxy history',
          },
        };
      }

        return toProxyHistoryToolResult({
          message: `Retrieved ${result.data.rows.length} proxy history row(s)`,
          data: {
            rows: result.data.rows.map((row) =>
              mapRequestMetadataRowToProxyHistoryRow({
                row,
                includeHeaders: false,
              }),
            ),
            total: result.data.lastRow,
            skip,
            limit,
          },
        });
    }
    case 'get': {
      if (!parsedInput.data.requestId) {
        return {
          status: 'error',
          result: { message: 'requestId is required for get action' },
        };
      }

      const result = await window.electronAPI.history.getRows({
        targetId,
        targetType,
        startRow: 0,
        endRow: 1,
        sortModel: [{ colId: 'timestamp', sort: 'desc' }],
        filters: {
          tqlWhereClause: 'r.id = ?',
          tqlParams: [parsedInput.data.requestId],
        },
      });

      if (!result.success || !result.data) {
        return {
          status: 'error',
          result: {
            message: result.error || 'Failed to get proxy history request',
          },
        };
      }

      const row = result.data.rows[0];
      if (!row) {
        return {
          status: 'error',
          result: {
            message: `Request ${parsedInput.data.requestId} not found in proxy history`,
          },
        };
      }

      const bodyResult = await window.electronAPI.bodyDb.get(
        parsedInput.data.requestId,
      );
      if (!bodyResult.success) {
        console.warn(
          '[proxyHistoryHandler] Failed to load request/response bodies',
          {
            requestId: parsedInput.data.requestId,
            error: bodyResult.error,
          },
        );
      }

      const requestBody = bodyResult.success
        ? bodyResult.data?.requestBody
        : undefined;
      const responseBody = bodyResult.success
        ? bodyResult.data?.responseBody
        : undefined;

        return toProxyHistoryToolResult({
          message: `Retrieved proxy history request ${parsedInput.data.requestId}`,
          data: {
            rows: [
              mapRequestMetadataRowToProxyHistoryRow({
                row,
                requestBody,
                responseBody,
                includeHeaders: true,
              }),
            ],
            total: 1,
            skip: 0,
            limit: 1,
          },
        });
    }
    default:
      return {
        status: 'error',
        result: { message: `Unknown action for ${PROXY_HISTORY_TOOL_NAME}` },
      };
  }
};
